import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef, ViewChild } from '@angular/core';
import { catchError, EMPTY, finalize, map, switchMap, throwError } from 'rxjs';
import { InputSearchDatePeriod } from '../../../shared/components/input/input.search.datePeriod';
import { DropdownCtx } from '../../../shared/directives/dropdown.directive';
import { IFilterDef, loadFilters, saveFilters, sortFilters } from '../../../shared/filter/filter';
import {
  AppscriptsNetworkInfo,
  AppscriptsNumber,
  AppscriptsNumbersFilter,
  AppscriptsRegistrationInfo,
  AppscriptsUpdateNumber,
  CLI_STATUSES,
  MO_STATUSES,
  MT_STATUSES,
  ProductStatusEnum,
} from '../../../shared/models/appscripts-user.model';
import { AppscriptUserFilterNetwork, ExtFilter } from '../../../shared/models/network.model';
import { AppscriptsUserRequestParams, AppscriptsUserService } from '../../../shared/services/appscripts-user.service';
import { LocalStorageService } from '../../../shared/services/localStorage.service';
import { ModalService } from '../../../shared/services/modal.service';
import { AllRequestParamsNetwork, NetInfoService } from '../../../shared/services/net-info.service';
import { NotificationService } from '../../../shared/services/notification.service';

@Component({
  standalone: false,
  selector: 'app-appscripts-user-filter',
  templateUrl: 'appscripts-user-filter.component.html',
  styleUrls: ['../../../shared/filter/filter.scss', 'appscripts-user-filter.component.scss'],
})
export class AppscriptsUserFilterComponent implements OnInit {
  @Output() onSubmit = new EventEmitter<AppscriptsNumbersFilter>();

  @ViewChild('phoneInfoModalTpl', { read: TemplateRef, static: false }) phoneInfoModalTpl: any;
  networkInfo: AppscriptsNetworkInfo;
  registrationInfo: AppscriptsRegistrationInfo;
  liveStatus: string;
  tsLoading = false;
  tsPhone = '';
  tsUpdateLoading = false;
  tsUpdateSuccessMessage = '';
  tsUpdateErrorMessage = '';

  @ViewChild(InputSearchDatePeriod, { static: false }) dateInput: InputSearchDatePeriod;

  networks: AppscriptUserFilterNetwork[] = [];
  networkSearch = '';
  mtStatuses = MT_STATUSES.map(s => ({ ...s, selected: false }));
  moStatuses = MO_STATUSES.map(s => ({ ...s, selected: false }));
  cliStatuses = CLI_STATUSES.map(s => ({ ...s, selected: false }));

  @Input() model: ExtFilter = {
    id: null,
    activeFrom: null,
    activeTo: null,
    balanceMin: null,
    balanceMax: null,
    mtSmsCountMin: null,
    mtSmsCountMax: null,
    moSmsCountMin: null,
    moSmsCountMax: null,
    phoneNumbers: null,
    mcc: null,
    mnc: null,
    originalMnc: null,
    referrerCode: null,
    referralCode: null,
    email: null,
    userId: null,
    paymentAddress: null,
    country: null,
    provider: null,
    moUserEnables: false,
    mtStatuses: null,
    moStatuses: null,
    cliStatuses: null,
  };
  loading = false;
  modelKeys: string[] = [];
  filterStateRestored = false;

  filters: IFilterDef[] = [
    { title: 'Last active', show: true, order: 1 },
    { title: 'Phone', show: true, order: 2 },
    { title: 'Network', show: true, order: 3 },
    { title: 'MT Sms', show: true, order: 4 },
    { title: 'Balance', show: true, order: 5 },
    { title: 'MCC', show: false, order: 6 },
    { title: 'MNC', show: false, order: 7 },
    { title: 'Original MNC', show: false, order: 8 },
    { title: 'Referral', show: false, order: 9 },
    { title: 'Referrer', show: false, order: 10 },
    { title: 'Email', show: false, order: 11 },
    { title: 'Payment address', show: false, order: 12 },
    { title: 'ID', show: false, order: 14 },
    { title: 'Status', show: true, order: 15 },
    { title: 'User ID', show: true, order: 16 },
  ];
  private readonly filterStorageKey = 'appscripts-numbers-filter';
  private readonly filterValuesStorageKey = 'appscripts-numbers-filter-values';
  private pendingNetworkSelections: { mcc: string; mnc: string }[] = [];

  constructor(
    public elementRef: ElementRef,
    public networkService: NetInfoService,
    public modemService: AppscriptsUserService,
    private storage: LocalStorageService,
    public modal: ModalService,
    private notificationService: NotificationService
  ) {
    this.modelKeys = Object.keys(this.model);
    loadFilters(storage, this.filterStorageKey, this.filters);
    this.filters = sortFilters(this.filters);
  }

  resetFilters() {
    this.filters.forEach(f => {
      if (f.value) {
        this.onChangeFilterValue('', f, false);
        if (f.title === 'Last active' && this.dateInput) {
          this.dateInput.reset();
        }
      }
    });
    this.onSubmitClick();
  }

  toggleFilter(filter: IFilterDef) {
    filter.show = !filter.show;
    if (filter.value && !filter.show) {
      this.onChangeFilterValue('', filter);
    }
  }

  getHiddenFilters() {
    return this.filters.filter(_ => !_.show);
  }

  getShowFilters() {
    return this.filters.filter(_ => _.show);
  }

  onClickShowFilter(filter: IFilterDef, dropdown: DropdownCtx) {
    filter.show = true;
    if (!this.getHiddenFilters().length) {
      dropdown.hide();
    }

    saveFilters(this.storage, this.filterStorageKey, this.filters);
  }

  ngOnInit() {
    const hasQueryParams = Object.keys(this.model).some(k => k !== 'moUserEnables');
    let filterStateLoaded = false;
    if (!hasQueryParams) {
      filterStateLoaded = this.loadFilterState();
    }

    this.loading = true;
    const requestParams = new AllRequestParamsNetwork();
    requestParams.setSort('name', 'asc');
    this.networkService.networks(requestParams).subscribe({
      next: networks => {
        this.networks = networks
          .map(n => {
            return {
              mcc: n.mcc,
              country: n.country.name,
              mnc: n.mnc,
              provider: n.name,
              selected: false,
              search: [n.mcc, n.country.name, n.mnc, n.name, n.mcc + n.mnc].join(''),
            };
          })
          .sort((a, b) => {
            return a.country.localeCompare(b.country) || a.provider.localeCompare(b.provider);
          });
        this.restoreNetworkSelections();
      },
      complete: () => {
        this.loading = false;
      },
    });

    let search = false;
    const defaults = {
      moUserEnables: false,
    };
    for (const i in this.model) {
      const exceptedValue = typeof defaults[i] !== 'undefined' ? defaults[i] : null;
      if (this.modelKeys.indexOf(i) === -1) {
        delete this.model[i];
      } else if (search === false && this.model[i] !== exceptedValue) {
        search = true;
      }
    }
    this.modelKeys.map(key => {
      if (!this.model[key]) {
        this.model[key] = null;
      }
    });

    if (filterStateLoaded) {
      this.filterStateRestored = true;
    } else if (search) {
      this.onSubmit.emit(this.model);
    }
  }

  onSubmitClick() {
    this.saveFilterState();
    this.onSubmit.emit({
      id: this.model.id,
      activeFrom: this.model.activeFrom,
      activeTo: this.model.activeTo,
      balanceMin: this.model.balanceMin,
      balanceMax: this.model.balanceMax,
      mtSmsCountMin: this.model.mtSmsCountMin,
      mtSmsCountMax: this.model.mtSmsCountMax,
      phoneNumbers: this.model.phoneNumbers,
      mcc: this.model.mcc,
      mnc: this.model.mnc,
      originalMnc: this.model.originalMnc,
      referrerCode: this.model.referrerCode,
      referralCode: this.model.referralCode,
      email: this.model.email,
      userId: this.model.userId,
      paymentAddress: this.model.paymentAddress,
      moUserEnables: this.model.moUserEnables,
      networks: this.model.networks,
      mtStatuses: this.model.mtStatuses,
      moStatuses: this.model.moStatuses,
      cliStatuses: this.model.cliStatuses,
    });
  }

  changeFilterValueByName(value: any, filterName: string, submit = true) {
    const filters = this.filters.filter(_ => _.title === filterName);
    if (filters.length) {
      this.onChangeFilterValue(value, filters[0], submit);
    }
  }

  getFilterByName(name: string): IFilterDef {
    return this.filters.filter(_ => _.title === name)[0];
  }

  isFilterActive(filter: IFilterDef): boolean {
    switch (filter.title) {
      case 'Last active':
        return !!(this.model.activeFrom || this.model.activeTo);
      case 'Phone':
        return !!this.model.phoneNumbers;
      case 'Network':
        return this.model.networks?.length > 0;
      case 'MT Sms':
        return this.model.mtSmsCountMin !== null || this.model.mtSmsCountMax !== null;
      case 'Balance':
        return this.model.balanceMin !== null || this.model.balanceMax !== null;
      case 'MCC':
        return this.model.mcc && this.model.mcc.length > 0;
      case 'MNC':
        return this.model.mnc && this.model.mnc.length > 0;
      case 'Original MNC':
        return this.model.originalMnc && this.model.originalMnc.length > 0;
      case 'Referral':
        return !!this.model.referralCode;
      case 'Referrer':
        return !!this.model.referrerCode;
      case 'Email':
        return !!this.model.email;
      case 'User ID':
        return !!this.model.userId;
      case 'Payment address':
        return !!this.model.paymentAddress;
      case 'ID':
        return !!this.model.id;
      case 'Status':
        return Boolean(this.model.moStatuses?.length || this.model.mtStatuses?.length || this.model.cliStatuses?.length);
      default:
        return false;
    }
  }
  onChangeFilterValue(value: any, filter: IFilterDef, submit = true) {
    switch (filter.title) {
      case 'Status': {
        if (value === '') {
          this.onClearAllStatuses();
        } else {
          const modelKey = value.product === 'mt' ? 'mtStatuses' : value.product === 'mo' ? 'moStatuses' : 'cliStatuses';

          const status = this[modelKey].find(s => s.id === value.status);
          if (status) {
            status.selected = value.selected;
          }
          this.model[modelKey] = this[modelKey].filter(s => s.selected).map(s => s.id);

          if (this.model[modelKey].length === 0) {
            this.model[modelKey] = null;
          }

          const selectedStatuses = [...this.mtStatuses, ...this.cliStatuses, ...this.moStatuses].filter(s => s.selected).length;

          filter.value = selectedStatuses > 0 ? `(${selectedStatuses})` : '';
        }

        break;
      }
      case 'Last active':
        if (value === '') {
          this.model.activeFrom = null;
          this.model.activeTo = null;
          filter.value = '';
        } else if (value.startIso && value.endIso) {
          this.model.activeFrom = value.startIso;
          this.model.activeTo = value.endIso;
          filter.value = value.startFormatStr + ' ' + value.endFormatStr;
        }
        break;
      case 'Phone':
        this.model.phoneNumbers = value;
        const numbersCount = value
          ?.split(' ')
          .map(p => p.trim())
          .filter(p => p !== '').length;
        filter.value = numbersCount > 0 ? `(${numbersCount})` : '';
        break;
      case 'Network':
        if (value === '') {
          this.networkSearch = '';
          this.networks.forEach(n => (n.selected = false));
          this.model.networks = [];
          filter.value = '';
        } else {
          this.model.networks = [];
          value.selected = !value.selected;
          this.networks.forEach(n => {
            if (n.selected) {
              this.model.networks.push(n);
            }
          });
          const mccFilter = this.getFilterByName('MCC');
          const mncFilter = this.getFilterByName('MNC');
          const originalMncFilter = this.getFilterByName('Original MNC');
          if (this.isFilterActive(mccFilter) || this.isFilterActive(mncFilter) || this.isFilterActive(originalMncFilter)) {
            this.notificationService.info('MCC, MNC and Original MNC filters are cleared when Network filter is applied.', 'Appscripts');
          }
          this.model.mcc = [];
          this.model.mnc = [];
          this.model.originalMnc = [];
          this.getFilterByName('MCC').value = '';
          this.getFilterByName('MNC').value = '';
          this.getFilterByName('Original MNC').value = '';
          const networkCount = this.model.networks.length;
          filter.value = networkCount > 0 ? `(${networkCount})` : '';
        }
        break;
      case 'MT Sms':
        if (value === '') {
          this.model.mtSmsCountMin = null;
          this.model.mtSmsCountMax = null;
          filter.value = '';
        } else {
          this.model.mtSmsCountMin = value.min ? value.min : null;
          this.model.mtSmsCountMax = value.max ? value.max : null;
          if (this.model.mtSmsCountMin !== null && this.model.mtSmsCountMax === null) {
            filter.value = `From ${value.min}`;
          } else if (this.model.mtSmsCountMin === null && this.model.mtSmsCountMax !== null) {
            filter.value = `To ${value.max}`;
          } else {
            filter.value = `(${value.min} - ${value.max})`;
          }
        }
        break;
      case 'Balance':
        if (value === '') {
          this.model.balanceMin = null;
          this.model.balanceMax = null;
          filter.value = '';
        } else {
          this.model.balanceMin = value.min ? value.min : null;
          this.model.balanceMax = value.max ? value.max : null;
          if (this.model.balanceMin !== null && this.model.balanceMax === null) {
            filter.value = `From ${value.min}`;
          } else if (this.model.balanceMin === null && this.model.balanceMax !== null) {
            filter.value = `To ${value.max}`;
          } else {
            filter.value = `(${value.min} - ${value.max})`;
          }
        }
        break;
      case 'MCC':
        this.model.mcc = value ? value : null;
        if (this.model.networks && this.model.networks.length > 0) {
          this.notificationService.info('Networks filter is cleared when MCC filter is applied.', 'Appscripts');
        }
        this.changeFilterValueByName('', 'Network', false);
        const mccCount = value
          ?.split(' ')
          .map(p => p.trim())
          .filter(p => p !== '').length;
        filter.value = mccCount > 0 ? `(${mccCount})` : '';
        break;
      case 'MNC':
        this.model.mnc = value ? value : null;
        if (this.model.networks && this.model.networks.length > 0) {
          this.notificationService.info('Networks filter is cleared when MNC filter is applied.', 'Appscripts');
        }
        this.changeFilterValueByName('', 'Network', false);
        const mncCount = value
          ?.split(' ')
          .map(p => p.trim())
          .filter(p => p !== '').length;
        filter.value = mncCount > 0 ? `(${mncCount})` : '';
        break;
      case 'Original MNC':
        this.model.originalMnc = value ? value : null;
        if (this.model.networks && this.model.networks.length > 0) {
          this.notificationService.info('Networks filter is cleared when Original MNC filter is applied.', 'Appscripts');
        }
        this.changeFilterValueByName('', 'Network', false);
        const originalMncCount = value
          ?.split(' ')
          .map(p => p.trim())
          .filter(p => p !== '').length;
        filter.value = originalMncCount > 0 ? `(${originalMncCount})` : '';
        break;
      case 'Referral':
        filter.value = value;
        this.model.referralCode = value ? value : null;
        break;
      case 'Referrer':
        filter.value = value;
        this.model.referrerCode = value ? value : null;
        break;
      case 'Email':
        filter.value = value;
        this.model.email = value ? value : null;
        break;
      case 'User ID':
        filter.value = value;
        this.model.userId = value ? value : null;
        break;
      case 'Payment address':
        filter.value = value;
        this.model.paymentAddress = value ? value : null;
        break;
      case 'ID':
        this.model.id = value;
        const idCount = value
          ? value
              .split(' ')
              .map(id => id.trim())
              .filter(id => id !== '').length
          : 0;
        filter.value = idCount > 0 ? `(${idCount})` : '';
        break;
    }
    if (submit) {
      this.onSubmitClick();
    }
  }

  onClickTs() {
    this.networkInfo = null;
    this.registrationInfo = null;
    this.liveStatus = null;
    this.tsPhone = '';
    this.resetTsUpdateState();
    this.modal.alert().component(this.phoneInfoModalTpl).open();
  }

  onClearAllStatuses(): void {
    this.mtStatuses.forEach(s => (s.selected = false));
    this.moStatuses.forEach(s => (s.selected = false));
    this.cliStatuses.forEach(s => (s.selected = false));
    this.model.mtStatuses = null;
    this.model.moStatuses = null;
    this.model.cliStatuses = null;
    this.filters.find(f => f.title === 'Status').value = null;

    this.onSubmitClick();
  }

  trackByStatusId(_: number, status: { id: ProductStatusEnum }): any {
    return status.id;
  }

  renderNetworkInfo(networkInfo: AppscriptsNetworkInfo): string {
    const { country, name, mcc, mnc, originalMnc, originalName } = networkInfo;

    const countryName = country?.name || 'Unknown';
    const networkName = name || 'Unknown';

    if (mcc == null && mnc == null) return countryName;

    const base = `${countryName}(${mcc})/${networkName}(${mnc})`;

    if (!originalMnc) return base;

    const provider = originalName || 'Unknown';

    return `${base} <span title="Ported from ${provider}"><i class="icon-ported"></i> ${provider}(${originalMnc})</span>`;
  }

  getProductStatusIcon(status: ProductStatusEnum) {
    switch (status) {
      case ProductStatusEnum.ENABLED:
        return 'icon-status-enabled';
      case ProductStatusEnum.ALLOWED:
        return 'icon-status-allowed';
      case ProductStatusEnum.NOT_ALLOWED:
        return 'icon-status-not-allowed';
      case ProductStatusEnum.OPTED_IN:
        return 'icon-status-opted-in';
    }
  }

  onSubmitTs(phone: string) {
    phone = phone.trim();
    this.tsPhone = phone;
    if (!phone) {
      return;
    }
    this.resetTsUpdateState();
    this.tsLoading = true;
    this.modemService
      .networkInfo(phone)
      .pipe(
        catchError(e => {
          this.tsLoading = false;
          this.notificationService.error({
            title: 'Numbers',
            message: 'An error occurred while loading xConnect data',
            serviceName: 'APPSCRIPTS',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
          return throwError(() => e);
        })
      )
      .subscribe(net => {
        this.networkInfo = net;
        this.tsLoading = false;
      });
    this.modemService
      .registrationInfo(phone)
      .pipe(
        catchError(e => {
          this.notificationService.error({
            title: 'Numbers',
            message: 'An error occurred while loading registration info',
            serviceName: 'APPSCRIPTS',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
          return throwError(() => e);
        })
      )
      .subscribe(_ => (this.registrationInfo = _));
    this.modemService
      .liveStatus(phone)
      .pipe(
        catchError(e => {
          this.notificationService.error({
            title: 'Numbers',
            message: 'An error occurred while loading live status',
            serviceName: 'APPSCRIPTS',
            requestMessage: e.statusText,
            requestCode: e.status,
          });
          return throwError(() => e);
        })
      )
      .subscribe(_ => (this.liveStatus = _));
  }

  onUpdatePhoneInfo() {
    if (!this.networkInfo || !this.tsPhone) {
      return;
    }
    this.tsUpdateLoading = true;
    this.tsUpdateSuccessMessage = '';
    this.tsUpdateErrorMessage = '';
    this.findNumberByPhone(this.tsPhone)
      .pipe(
        switchMap(number => {
          if (!number) {
            return throwError(() => new Error('PHONE_NOT_FOUND'));
          }
          if (this.isPhoneInfoUpToDate(number)) {
            this.tsUpdateSuccessMessage = 'Already up to date message';
            return EMPTY;
          }
          const payload = this.buildUpdatePayload(number);
          return this.modemService.updateNumber(number.id, payload);
        }),
        finalize(() => (this.tsUpdateLoading = false))
      )
      .subscribe({
        next: () => {
          this.tsUpdateSuccessMessage = 'Phone information successfully updated in the system.';
        },
        error: error => {
          if (error?.message === 'PHONE_NOT_FOUND' || error?.status === 404) {
            this.tsUpdateErrorMessage = 'Phone number not found in Appscripts.';
            return;
          }
          this.tsUpdateErrorMessage = `Couldn't update phone info: ${this.getUpdateErrorDetails(error)}`;
        },
      });
  }

  private buildUpdatePayload(number: AppscriptsNumber): AppscriptsUpdateNumber {
    return {
      phoneNumber: number.phoneNumber,
      mcc: this.networkInfo?.mcc ?? number.mcc,
      mnc: this.networkInfo?.mnc ?? number.mnc,
      originalMnc: this.networkInfo?.originalMnc ?? number.originalMnc,
      blocked: number.blocked,
      updateHlr: number.updateHlr,
      mtEnabled: number.mtEnabled,
      mtAllowed: number.mtAllowed,
      moEnabled: number.moEnabled,
      moOptedIn: number.moOptedIn,
      moAllowed: number.moAllowed,
      cliEnabled: number.cliEnabled,
      cliOptedIn: number.cliOptedIn,
      cliAllowed: number.cliAllowed,
      comment: number.comment,
    };
  }

  private isPhoneInfoUpToDate(number: AppscriptsNumber): boolean {
    const storedMcc = this.normalizeNetworkValue(number.mcc);
    const storedMnc = this.normalizeNetworkValue(number.mnc);
    const storedOriginalMnc = this.normalizeNetworkValue(number.originalMnc);
    const newMcc = this.normalizeNetworkValue(this.networkInfo?.mcc);
    const newMnc = this.normalizeNetworkValue(this.networkInfo?.mnc);
    const newOriginalMnc = this.normalizeNetworkValue(this.networkInfo?.originalMnc);

    return storedMcc === newMcc && storedMnc === newMnc && storedOriginalMnc === newOriginalMnc;
  }

  private normalizeNetworkValue(value?: string | null): string {
    return value ? value.trim() : '';
  }

  canUpdatePhoneInfo(): boolean {
    return !!this.networkInfo && !!this.tsPhone?.trim();
  }

  private findNumberByPhone(phone: string) {
    const requestParams = new AppscriptsUserRequestParams();
    requestParams.page = 0;
    requestParams.size = 1;
    const filter = {
      phoneNumbers: '',
      moUserEnables: false,
    } as AppscriptsNumbersFilter;
    (filter as any).phoneNumbers = [phone];
    requestParams.filter = filter;
    return this.modemService.all(requestParams).pipe(
      map(collection => {
        const number = collection.content?.find(n => n.phoneNumber === phone) ?? collection.content?.[0];
        if (!number) {
          throw new Error('PHONE_NOT_FOUND');
        }
        return number;
      })
    );
  }

  private getUpdateErrorDetails(error: any): string {
    if (!error) {
      return 'Unknown error';
    }
    if (error.error) {
      if (typeof error.error === 'string') {
        return error.error;
      }
      if (error.error.message) {
        return error.error.message;
      }
    }
    if (error.message) {
      return error.message;
    }
    if (error.statusText) {
      return error.statusText;
    }
    return 'Unknown error';
  }

  private resetTsUpdateState(): void {
    this.tsUpdateLoading = false;
    this.tsUpdateSuccessMessage = '';
    this.tsUpdateErrorMessage = '';
  }

  private saveFilterState() {
    const state = {
      model: {
        id: this.model.id,
        activeFrom: this.model.activeFrom,
        activeTo: this.model.activeTo,
        balanceMin: this.model.balanceMin,
        balanceMax: this.model.balanceMax,
        mtSmsCountMin: this.model.mtSmsCountMin,
        mtSmsCountMax: this.model.mtSmsCountMax,
        phoneNumbers: this.model.phoneNumbers,
        mcc: this.model.mcc,
        mnc: this.model.mnc,
        originalMnc: this.model.originalMnc,
        referrerCode: this.model.referrerCode,
        referralCode: this.model.referralCode,
        email: this.model.email,
        userId: this.model.userId,
        paymentAddress: this.model.paymentAddress,
        moUserEnables: this.model.moUserEnables,
        mtStatuses: this.model.mtStatuses,
        moStatuses: this.model.moStatuses,
        cliStatuses: this.model.cliStatuses,
      },
      filterValues: this.filters.map(f => ({ title: f.title, value: f.value || null })),
      mtStatuses: this.mtStatuses.map(s => ({ id: s.id, selected: s.selected })),
      moStatuses: this.moStatuses.map(s => ({ id: s.id, selected: s.selected })),
      cliStatuses: this.cliStatuses.map(s => ({ id: s.id, selected: s.selected })),
      selectedNetworks: this.networks.filter(n => n.selected).map(n => ({ mcc: n.mcc, mnc: n.mnc })),
    };
    this.storage.set(this.filterValuesStorageKey, state);
  }

  private loadFilterState(): boolean {
    const state = this.storage.get(this.filterValuesStorageKey);
    if (!state) return false;

    if (state.model) {
      for (const key of Object.keys(state.model)) {
        if (this.modelKeys.includes(key)) {
          this.model[key] = state.model[key];
        }
      }
    }

    if (state.filterValues) {
      state.filterValues.forEach(saved => {
        const filter = this.filters.find(f => f.title === saved.title);
        if (filter) {
          filter.value = saved.value;
        }
      });
    }

    if (state.mtStatuses) {
      state.mtStatuses.forEach(saved => {
        const status = this.mtStatuses.find(s => s.id === saved.id);
        if (status) status.selected = saved.selected;
      });
    }
    if (state.moStatuses) {
      state.moStatuses.forEach(saved => {
        const status = this.moStatuses.find(s => s.id === saved.id);
        if (status) status.selected = saved.selected;
      });
    }
    if (state.cliStatuses) {
      state.cliStatuses.forEach(saved => {
        const status = this.cliStatuses.find(s => s.id === saved.id);
        if (status) status.selected = saved.selected;
      });
    }

    if (state.selectedNetworks?.length > 0) {
      this.pendingNetworkSelections = state.selectedNetworks;
    }

    return true;
  }

  private restoreNetworkSelections() {
    if (!this.pendingNetworkSelections?.length) return;

    const networkFilter = this.getFilterByName('Network');
    this.model.networks = [];
    this.pendingNetworkSelections.forEach(saved => {
      const network = this.networks.find(n => n.mcc === saved.mcc && n.mnc === saved.mnc);
      if (network) {
        network.selected = true;
        this.model.networks.push(network);
      }
    });
    this.pendingNetworkSelections = [];

    if (this.model.networks.length > 0) {
      networkFilter.value = `(${this.model.networks.length})`;
      this.onSubmitClick();
    }
  }

  onDropFilter(event: CdkDragDrop<any, any, IFilterDef>) {
    const filter = event.item.data;
    if (!filter.show) {
      return;
    }
    this.toggleFilter(filter);
    saveFilters(this.storage, this.filterStorageKey, this.filters);
  }

  onDropPosFilter(event: CdkDragDrop<any, any, IFilterDef>) {
    moveItemInArray<IFilterDef>(this.filters, event.previousIndex, event.currentIndex);
    this.filters.forEach((f, i) => (f.order = i + 1));
    this.filters = sortFilters(this.filters);
    saveFilters(this.storage, this.filterStorageKey, this.filters);
  }

  onShowFilter(filter: IFilterDef, dropdownContent: TemplateRef<any>) {
    const el = dropdownContent.elementRef.nativeElement.parentNode as HTMLElement;
    setTimeout(() => {
      const input = el.querySelector('.dropdown-menu .form-control') as HTMLElement;
      if (input) {
        input.focus();
      }
    });
  }
}
